import {
  compareTankNames,
  dayPlus,
  daysApart,
  dec,
  fillLimitLiters,
  fillsTank,
  staysOverlap,
  usualDaysIn,
} from "@bl/domain";
import type { Tank, TankStay, TankStayPatch, UnbookedBrew } from "./api";
import { showDate } from "./format";
import type { TankZoom } from "./layout/layout";
import { capacityLabel, totalLabel } from "./tankLayout";

/**
 * The tank schedule on Production (Scott's Jam, 2026-10-03; built as Tyler adjusted it, 2026-10-05): a
 * row per tank and a column per day, each beer's time in a tank a bar across them. These are the parts
 * of it that are worked out rather than drawn.
 */

/** What each stop on the zoom slider is called, from its - end to its +. */
export const ZOOM_LABELS: Record<TankZoom, string> = {
  7: "1 week",
  14: "2 weeks",
  31: "1 month",
  91: "Quarter",
  182: "6 months",
  365: "1 year",
};

/** The Monday of a date's week. The chart starts on one, so its weekends fall in step. */
export function mondayOf(on: string): string {
  const weekday = new Date(`${on}T00:00:00Z`).getUTCDay();
  return dayPlus(on, -((weekday + 6) % 7));
}

export interface Span {
  from: string;
  to: string;
}

/**
 * The days loaded around one, enough to scroll a few screens either way at a zoom; the chart loads more
 * as it is scrolled toward either end, so it never stops at a horizon. A span already wider is kept.
 */
export function spanAround(on: string, zoom: number, span?: Span): Span {
  const from = mondayOf(dayPlus(on, -Math.max(21, zoom * 3)));
  const to = dayPlus(on, Math.max(60, zoom * 4));
  if (!span) return { from, to };
  return { from: from < span.from ? from : span.from, to: to > span.to ? to : span.to };
}

/** How far the chart reaches each time it is scrolled near an end: whole weeks, two screens or so. */
export const reachFor = (zoom: number) => Math.max(4, Math.ceil((zoom * 2) / 7)) * 7;

/** A day at least this wide, in pixels, is a column of its own; narrower, the chart goes by weeks. */
export const DAY_COLUMN = 14;

/**
 * What the chart's header marks across, and where its lines fall (Scott's Jam, 2026-10-08: "Days of the
 * week need to be listed when viewing in shorter timelines, weeks displayed for longer. Lines should
 * separate days of the week in shorter timelines and weeks in longer timelines"). Months always; then
 * each day, with its weekday, while a day is a column of its own; else each week, by its Monday's date
 * (Tyler, 2026-10-19). `x` and `w` are pixels from the chart's left.
 */
export interface TimeScale {
  unit: "day" | "week";
  months: { on: string; x: number; w: number; label: string }[];
  /** A day's weekday, and its date while two digits fit beside each other, else every other one's. */
  days: { on: string; x: number; weekday: string; date: string | null }[];
  /** A week from its Monday; only some are labelled once weeks are too narrow for every date. */
  weeks: { on: string; x: number; label: string | null }[];
  /** Between days, or weeks; where a month starts among the days, darker. */
  lines: { on: string; x: number; month: boolean }[];
}

const monthShort = new Intl.DateTimeFormat(undefined, { month: "short", timeZone: "UTC" });
const monthLong = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const weekdayShort = new Intl.DateTimeFormat(undefined, { weekday: "short", timeZone: "UTC" });
const weekdayLetter = new Intl.DateTimeFormat(undefined, { weekday: "narrow", timeZone: "UTC" });
const utc = (on: string) => new Date(`${on}T00:00:00Z`);
/** A Monday long ago, so the weeks labelled when not all fit stay the same as the chart loads more. */
const FIRST_MONDAY = "1970-01-05";

export function timeScale(from: string, total: number, dw: number): TimeScale {
  const unit = dw >= DAY_COLUMN ? "day" : "week";
  const week = 7 * dw;
  // Every Monday's date while two digits fit, then every other one, then every fourth.
  const every = week >= 22 ? 1 : week >= 11 ? 2 : 4;
  const scale: TimeScale = { unit, months: [], days: [], weeks: [], lines: [] };
  for (let i = 0; i < total; i++) {
    const on = dayPlus(from, i);
    const d = utc(on);
    const date = d.getUTCDate();
    const x = i * dw;
    if (date === 1 || i === 0) {
      let j = i + 1;
      while (j < total && utc(dayPlus(from, j)).getUTCDate() !== 1) j++;
      const label = dw * 30 > 120 ? monthLong.format(d) : monthShort.format(d);
      scale.months.push({ on, x, w: (j - i) * dw, label });
    }
    const monday = d.getUTCDay() === 1;
    if (unit === "day") {
      scale.days.push({
        on,
        x,
        weekday: (dw >= 40 ? weekdayShort : weekdayLetter).format(d),
        date: dw >= 22 || date % 2 === 1 ? String(date) : null,
      });
      if (i > 0) scale.lines.push({ on, x, month: date === 1 });
    } else if (monday) {
      const labelled = (daysApart(FIRST_MONDAY, on) / 7) % every === 0;
      scale.weeks.push({ on, x, label: labelled ? String(date) : null });
      if (i > 0) scale.lines.push({ on, x, month: false });
    }
  }
  return scale;
}

export type Tone = "done" | "in" | "planned";

/** A bar: one beer's time in one tank, or a double batch, two brews of one recipe filling it together. */
export interface Bar {
  key: string;
  /** Its tank; none for a brew not yet given one. */
  tankId: string | null;
  /** The stays it draws; none for a brew with no tank. */
  stays: TankStay[];
  brews: TankStay["brew"][];
  label: string;
  from: string;
  /** Its last day; none while nobody has said when it comes out. */
  to: string | null;
  tone: Tone;
  /** Another beer in the tank at the same time, or more than it holds. */
  clash: boolean;
  overdue: boolean;
  /** A fermenter with no brite tank booked after it, where the beer goes to one. */
  noBrite: boolean;
  lane: number;
}

/** "Blonde Ale #9", "Blonde Ale #9 + #10", or "Blonde Ale ×2" for brews not numbered yet. */
const numbered = (brews: TankStay["brew"][]) => {
  const [first] = brews;
  const numbers = brews.map((b) => b.number).filter((n): n is number => n !== null);
  const name = first?.recipe.name ?? "";
  if (numbers.length === brews.length && numbers.length) {
    return `${name} ${numbers.map((n) => `#${n}`).join(" + ")}`;
  }
  return brews.length > 1 ? `${name} ×${brews.length}` : name;
};

function toneOf(stays: TankStay[]): Tone {
  if (stays.some((s) => s.state === "in")) return "in";
  if (stays.every((s) => s.state === "emptied")) return "done";
  return "planned";
}

const spanOf = (s: { inOn: string; lastOn: string | null }) => ({ inOn: s.inOn, lastOn: s.lastOn });

/**
 * A tank's stays as bars. Two brews of one recipe in it at once are one bar, "Blonde Ale #9 + #10",
 * which the server tells apart from a double booking: a stay lists the other beers in its tank then, and
 * a double batch is not one of them.
 */
export function barsOf(
  stays: readonly TankStay[],
  { hasBrites, all }: { hasBrites: boolean; all: readonly TankStay[] },
): Bar[] {
  const sorted = [...stays].sort((a, b) => a.inOn.localeCompare(b.inOn));
  const groups: TankStay[][] = [];
  for (const s of sorted) {
    const batch = groups.find((g) =>
      g.some(
        (o) =>
          o.brew.id !== s.brew.id &&
          o.brew.recipe.name === s.brew.recipe.name &&
          !s.doubleBooked.some((d) => d.stayId === o.id) &&
          !o.doubleBooked.some((d) => d.stayId === s.id) &&
          staysOverlap(spanOf(o), spanOf(s)),
      ),
    );
    if (batch) batch.push(s);
    else groups.push([s]);
  }
  return groups.map((g) => {
    const brews = g.map((s) => s.brew).sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
    const ends = g.map((s) => s.lastOn);
    const tone = toneOf(g);
    const fermenter = g[0]?.tank.kind === "Fermenter";
    // Where a brew is booked into a brite tank anywhere in view, it is on its way to one.
    const briteFor = (brewId: string) =>
      all.some((o) => o.brew.id === brewId && o.tank.kind === "Brite tank");
    return {
      key: g.map((s) => s.id).join("+"),
      tankId: g[0]?.tank.id ?? null,
      stays: g,
      brews,
      label: numbered(brews),
      from: g.map((s) => s.inOn).sort()[0] ?? "",
      to: ends.includes(null) ? null : ((ends as string[]).sort().at(-1) ?? null),
      tone,
      clash: g.some((s) => s.doubleBooked.length > 0 || s.overCapacity),
      overdue: g.some((s) => s.overdue),
      noBrite:
        hasBrites &&
        fermenter &&
        tone !== "done" &&
        g.some((s) => s.brew.tankDays.brite > 0 && !briteFor(s.brew.id)),
      lane: 0,
    };
  });
}

/**
 * The brew a bar opens straight to its brew day, if any: one whose brew day has started (Scott's Jam,
 * 2026-10-08: "active brew days should be accessible by clicking the beer in the production tab").
 * Any other bar opens its brew's tanks.
 */
export const brewingIn = (bar: Pick<Bar, "brews">) =>
  bar.brews.find((b) => b.status === "draft") ?? null;

/** A brew with no tank yet, drawn for as long as it would be in a fermenter. */
export function unbookedBar(u: UnbookedBrew): Bar {
  const b = u.brew;
  return {
    key: `brew:${b.id}`,
    tankId: null,
    stays: [],
    brews: [b],
    label: numbered([b]),
    from: b.brewedOn,
    to: dayPlus(b.brewedOn, b.tankDays.fermenter),
    tone: "planned",
    clash: false,
    overdue: false,
    noBrite: false,
    lane: 0,
  };
}

/** Another beer in a stay's tank at the same time, as the stay lists it. */
export type Sharing = TankStay["doubleBooked"][number];

/**
 * A warning about another beer in the tank, in Scott's words (Jam, 2026-10-08: "it needs to say
 * something like tank is full ... and if I have something scheduled it should say something like tank
 * is scheduled"): full once that beer is in it or brewing into it, scheduled before (`fillsTank`).
 */
export function sharingNote(tankName: string, o: Sharing): string {
  const name = `${o.recipeName}${o.number === null ? "" : ` brew ${o.number}`}`;
  const until = o.lastOn ? ` until ${showDate(o.lastOn)}` : "";
  if (o.state === "emptied") return `${tankName} had ${name} in it${until}`;
  if (!o.full) return `${tankName} is scheduled for ${name}${until}`;
  return `${tankName} is full${until}: ${name} is ${o.state === "in" ? "in it" : "brewing into it"}`;
}

/** What is wrong with a stay among the others in its tank, a line each, as its warnings say it. */
export function stayNotes(s: TankStay): string[] {
  return [
    ...s.doubleBooked.map((o) => sharingNote(s.tank.name, o)),
    ...(s.overCapacity ? [`Too much for ${s.tank.name} at once`] : []),
    ...(s.outOfService ? [`${s.tank.name} is out of service`] : []),
  ];
}

/**
 * Bars that share a row stacked so none covers another: each goes in the first lane free by its first
 * day. One coming out the day the next goes in shares the lane, as a transfer day does the tank.
 * Returns how many lanes the row needs.
 */
export function stack(bars: Bar[]): number {
  const ends: (string | null)[] = [];
  for (const bar of [...bars].sort((a, b) => a.from.localeCompare(b.from))) {
    let lane = ends.findIndex((end) => end !== null && end <= bar.from);
    if (lane < 0) {
      lane = ends.length;
      ends.push(bar.to);
    } else ends[lane] = bar.to;
    bar.lane = lane;
  }
  return Math.max(1, ends.length);
}

/**
 * Under each tank's name, when it is free (Scott: "it would be helpful to know when bright tanks are
 * free"): from today, past every beer in it or booked into it back to back. A tank is free again the day
 * a beer comes out, since another can go in that day.
 */
export function freeNote(t: Tank, today: string): { text: string; free: boolean } {
  if (t.outOfService) return { text: "Out of service", free: false };
  const late = t.stays.find((s) => s.overdue);
  if (late?.dueOutOn) return { text: `Was due out ${showDate(late.dueOutOn)}`, free: false };
  let at = today;
  for (;;) {
    const holding = t.stays.find(
      (s) => s.inOn <= at && (s.lastOn === null || s.lastOn > at) && s.state !== "emptied",
    );
    if (!holding) break;
    if (holding.lastOn === null) return { text: "No day out set", free: false };
    at = holding.lastOn;
  }
  // "Free from" a day, so it is not read as free until it (the re-critique, 2026-10-07).
  if (at !== today) return { text: `Free from ${showDate(at)}`, free: false };
  const next = t.stays
    .filter((s) => s.inOn > today && s.state !== "emptied")
    .map((s) => s.inOn)
    .sort()[0];
  return { text: next ? `Free until ${showDate(next)}` : "Free now", free: true };
}

/** A drag let go: a bar moved some days, onto a row, or its start or end stretched some days. */
export interface Drop {
  bar: Bar;
  mode: "move" | "start" | "end";
  days: number;
  /** The row it was let go over: a tank, the No tank yet lane (null), or neither. */
  row: string | null | undefined;
}

export type Step =
  /** Its bookings move with it, unless `keepBookings`. */
  | { kind: "brewDay"; brewId: string; on: string; keepBookings?: boolean }
  | { kind: "change"; stayId: string; patch: TankStayPatch }
  | { kind: "book"; brewId: string; tankId: string; inOn: string };

/**
 * What a drop asks the server to do, or why it cannot. A booking that starts on its brew's day moves
 * the brew, as the Calendar's day for it is the day it goes in, and the server takes its other bookings
 * along. A beer in a tank went in on its day: only its end stretches, and a transfer is Move to.
 *
 * A booking's start stretches too (Tyler, 2026-10-05: "I already can later by dragging right, id like
 * to be able to drag left"), its end staying put. One that starts on its planned brew's day takes the
 * brew day with it, brewing sooner or later, and the brew's other bookings stay on their days; any other
 * starts on its brew day or after.
 */
export function stepsFor(
  { bar, mode, days, row }: Drop,
  { outOfService }: { outOfService: (tankId: string) => boolean },
): Step[] | string {
  const toTank = typeof row === "string" && row !== bar.tankId ? row : null;
  if (toTank && outOfService(toTank)) return "That tank is out of service";
  if (bar.stays.length === 0) {
    const b = bar.brews[0];
    // Along No tank yet, or let go off every tank, it goes back where it was: its day is not moved
    // from there (Tyler, 2026-10-05: "i don't want to be able to change the schedule that way").
    if (!b || mode !== "move" || !toTank) return [];
    const on = dayPlus(b.brewedOn, days);
    const steps: Step[] = [];
    if (days !== 0) {
      if (b.status !== "planned") return "Brew day has started, so its day stays";
      steps.push({ kind: "brewDay", brewId: b.id, on });
    }
    steps.push({ kind: "book", brewId: b.id, tankId: toTank, inOn: on });
    return steps;
  }
  if (bar.stays.length > 1) return "A double batch is changed from its brews";
  const s = bar.stays[0] as TankStay;
  if (s.state === "emptied") return `${s.brew.recipe.name} is out of ${s.tank.name}`;
  if (mode === "end") {
    if (days === 0 || s.lastOn === null) return [];
    const dueOutOn = dayPlus(s.lastOn, days);
    if (dueOutOn < s.inOn) return "It cannot come out before it goes in";
    return [{ kind: "change", stayId: s.id, patch: { dueOutOn } }];
  }
  if (mode === "start") {
    if (s.state === "in") return `${s.brew.recipe.name} went into ${s.tank.name} on its day`;
    if (days === 0) return [];
    const inOn = dayPlus(s.inOn, days);
    if (s.lastOn !== null && inOn > s.lastOn) return "It cannot go in after it comes out";
    const change: Step = { kind: "change", stayId: s.id, patch: { inOn } };
    if (s.inOn === s.brew.brewedOn && s.brew.status === "planned") {
      const brewDay: Step = { kind: "brewDay", brewId: s.brew.id, on: inOn, keepBookings: true };
      // Each request valid on its own: sooner, the brew day goes first; later, the booking does.
      return days < 0 ? [brewDay, change] : [change, brewDay];
    }
    if (s.brew.status !== "brewed" && inOn < s.brew.brewedOn) {
      return "It goes into a tank on its brew day or after";
    }
    return [change];
  }
  if (s.state === "in") {
    if (days === 0 && !toTank) return [];
    return toTank
      ? `${s.brew.recipe.name} is in ${s.tank.name}: open it to move it`
      : `${s.brew.recipe.name} went into ${s.tank.name} on its day`;
  }
  const steps: Step[] = [];
  const startsOnBrewDay = s.inOn === s.brew.brewedOn && s.brew.status === "planned";
  if (days !== 0 && startsOnBrewDay) {
    steps.push({ kind: "brewDay", brewId: s.brew.id, on: dayPlus(s.brew.brewedOn, days) });
  } else if (days !== 0) {
    const inOn = dayPlus(s.inOn, days);
    if (s.brew.status !== "brewed" && inOn < s.brew.brewedOn) {
      return "It goes into a tank on its brew day or after";
    }
    steps.push({
      kind: "change",
      stayId: s.id,
      patch: { inOn, dueOutOn: s.dueOutOn === null ? null : dayPlus(s.dueOutOn, days) },
    });
  }
  if (toTank) steps.push({ kind: "change", stayId: s.id, patch: { tankId: toTank } });
  return steps;
}

/** The days of a drop, said back while it is dragged: "Oct 12 to Oct 26". */
export function dropDays({ bar, mode, days }: Drop): string {
  const from = mode === "end" ? bar.from : dayPlus(bar.from, days);
  const to = bar.to === null || mode === "start" ? bar.to : dayPlus(bar.to, days);
  return to === null ? `From ${showDate(from)}` : `${showDate(from)} to ${showDate(to)}`;
}

/**
 * Where a bar dragged onto another tank's row would land in it, drawn there while it is dragged so the
 * days it would take show against that tank's (Tyler, 2026-10-05: "when you grab a batch with no tank
 * can we have the box for it follow your cursor and expand onto the FV layout with the dates?"). A brew
 * with no tank yet is booked from its brew day for its usual days in a tank of that kind, as the server
 * books it, so it is longer over a fermenter than over a brite tank; a booking keeps its days. It
 * clashes where another beer is in that tank on those days; a second brew of the same recipe is a
 * double batch, asked about when it is dropped (`askBefore`). Nothing lands in a tank out of service
 * or retired.
 */
export interface Landing {
  from: string;
  /** Its last day; none where a tank of that kind has no usual days. */
  to: string | null;
  /** Another beer in that tank then: full where one is in it or brewing into it, else scheduled. */
  clash: false | "full" | "scheduled";
}

export function landing(
  drop: Drop,
  tank: Pick<Tank, "id" | "kind" | "outOfService" | "retired">,
  stays: readonly TankStay[],
): Landing | null {
  const { bar, mode, row } = drop;
  if (mode !== "move" || row !== tank.id || row === bar.tankId) return null;
  if (tank.outOfService || tank.retired) return null;
  const span = daysAfter(drop, tank.kind);
  if (!span) return null;
  const recipe = bar.brews[0]?.recipe.id;
  const others = alongside(drop, tank.id, span, stays).filter((s) => s.brew.recipe.id !== recipe);
  const clash: Landing["clash"] =
    others.length === 0 ? false : others.some((s) => fillsTank(s, s.brew)) ? "full" : "scheduled";
  return { ...span, clash };
}

/**
 * The days a bar would take once dropped: a brew with no tank from its brew day for its usual days in a
 * tank of that kind, as the server books it; a booking moved with its own days, or stretched at its end.
 */
function daysAfter(
  { bar, mode, days }: Drop,
  kind: string,
): { from: string; to: string | null } | null {
  if (bar.stays.length === 0) {
    const b = bar.brews[0];
    if (!b) return null;
    const from = dayPlus(bar.from, days);
    const usual = usualDaysIn(kind, b.tankDays);
    return { from, to: usual === null ? null : dayPlus(from, usual) };
  }
  const later = (on: string | null) => (on === null ? null : dayPlus(on, days));
  if (mode === "end") return { from: bar.from, to: later(bar.to) };
  if (mode === "start") return { from: dayPlus(bar.from, days), to: bar.to };
  return { from: dayPlus(bar.from, days), to: later(bar.to) };
}

/** The stays a dropped bar would share its tank with, on any of its days. */
function alongside(
  { bar }: Drop,
  tankId: string,
  span: { from: string; to: string | null },
  stays: readonly TankStay[],
): TankStay[] {
  const mine = new Set(bar.stays.map((s) => s.id));
  return stays.filter(
    (s) =>
      s.tank.id === tankId &&
      s.state !== "emptied" &&
      !mine.has(s.id) &&
      staysOverlap({ inOn: span.from, lastOn: span.to }, s),
  );
}

/**
 * What to ask before a drop is made, so neither happens by a slip of the mouse (Tyler, 2026-10-05:
 * "when i drop 2 brews onto each other on 1 fermenter instead of accepting it and doing brew x2, can
 * we ask for confirmation to combine those", and "a warning when dragging to a hidden fermenter"):
 * the brews of the same recipe it would make a double batch with in its tank, and the tank, when it
 * is hidden from the schedule and the bar is not already in it. Nothing to ask is null.
 */
export interface DropQuestion {
  tank: Tank;
  from: string;
  to: string | null;
  /** The brews of its recipe it would share the tank with, as one double batch. */
  combines: TankStay["brew"][];
  /** Into a tank hidden from the schedule. */
  hidden: boolean;
}

export function askBefore(
  drop: Drop,
  tanks: readonly Tank[],
  stays: readonly TankStay[],
): DropQuestion | null {
  const { bar, mode, row } = drop;
  const toTank = mode === "move" && typeof row === "string" ? row : null;
  const tank = tanks.find((t) => t.id === (toTank ?? bar.tankId));
  if (!tank) return null;
  const span = daysAfter(drop, tank.kind);
  if (!span) return null;
  const recipe = bar.brews[0]?.recipe.id;
  const combines = alongside(drop, tank.id, span, stays)
    .filter((s) => s.brew.recipe.id === recipe)
    .map((s) => s.brew);
  const hidden = tank.hidden && tank.id !== bar.tankId;
  return combines.length || hidden ? { tank, ...span, combines, hidden } : null;
}

/**
 * A brew as a drop names it: "Blonde Ale #9", or, before it has a number, by its day, "the Oct 15
 * Blonde Ale", so two brews of one recipe read apart.
 */
export const brewName = (b: TankStay["brew"]) =>
  b.number === null
    ? `the ${showDate(b.brewedOn)} ${b.recipe.name}`
    : `${b.recipe.name} #${b.number}`;

/**
 * The lane of its row a landing is drawn in: the first whose bars it would not cover, as `stack` would
 * place it once dropped, or a new one below them, so it never hides the beer it lands beside.
 */
export function laneFor(l: Landing, bars: readonly Bar[], lanes: number): number {
  const span = { inOn: l.from, lastOn: l.to };
  for (let lane = 0; lane < lanes; lane++) {
    const taken = bars.some(
      (b) => b.lane === lane && staysOverlap(span, { inOn: b.from, lastOn: b.to }),
    );
    if (!taken) return lane;
  }
  return lanes;
}

/** The days a bar would land on, said back while it is dragged: "Oct 12 to Oct 29". */
export const landingDays = ({ from, to }: Landing) =>
  to === null ? `From ${showDate(from)}` : `${showDate(from)} to ${showDate(to)}`;

export interface FermenterChoice {
  tank: Tank;
  /** Free for the whole stay, and big enough for the batch. */
  free: boolean;
  /** Another beer in it then: full where it is in it or brewing into it, else scheduled. */
  busy: "full" | "scheduled" | null;
  /** Why not, or that it would be a double batch. */
  note: string;
}

/**
 * The fermenters a brew could go into, for Add a brew's Fermenter field (from the tank schedule's
 * proposal, approved 2026-10-05): those free for its usual days from its brew day that take its batch
 * (`fillLimitLiters`) first, smallest first, then the rest, each saying why not: full or scheduled for
 * another beer (Scott's Jam, 2026-10-08), or too small. A brew of the same recipe in one already is a
 * double batch, which is allowed. Not one is out of the question but out of service.
 */
export function fermenterChoices(
  tanks: readonly Tank[],
  want: { on: string; days: number; liters: string | null; recipe: string | null },
): FermenterChoice[] {
  const span = { inOn: want.on, lastOn: dayPlus(want.on, want.days) };
  const choices = tanks
    .filter((t) => t.kind === "Fermenter" && !t.retired)
    .map((t) => {
      const other = t.stays.find((s) => s.state !== "emptied" && staysOverlap(span, s));
      const small =
        want.liters !== null && dec(want.liters).round(0).gt(fillLimitLiters(t).round(0));
      const until = other?.lastOn ? ` until ${showDate(other.lastOn)}` : "";
      const name = other?.brew.recipe.name;
      const busy: FermenterChoice["busy"] =
        t.outOfService || !other || name === want.recipe
          ? null
          : fillsTank(other, other.brew)
            ? "full"
            : "scheduled";
      const note = t.outOfService
        ? "out of service"
        : other && name === want.recipe
          ? `${name} in it: a double batch`
          : other
            ? `${busy === "full" ? "full with" : "scheduled for"} ${name}${until}`
            : small
              ? t.totalVolume === null
                ? `holds ${capacityLabel(t)}`
                : `fills past 90% of its ${totalLabel(t)}`
              : "";
      const free = note === "" || (other !== undefined && name === want.recipe);
      return { tank: t, free, busy, note };
    });
  const rank = (c: FermenterChoice) => (c.free ? 0 : c.tank.outOfService ? 2 : 1);
  return choices.sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (a.free && b.free ? dec(a.tank.capacityLiters).cmp(dec(b.tank.capacityLiters)) : 0) ||
      compareTankNames(a.tank.name, b.tank.name),
  );
}
